// ProtectedRoute.tsx
import React, {useEffect, useState, useRef} from "react";
import {useSearchParams, Outlet, useNavigate} from "react-router-dom";
import {
  ingestPasswordlessAuthToken,
  refreshToken,
} from "../services/authService";
import {
  selectAccessToken,
  selectAuthenticated,
  selectTokenIsAlmostExpired,
  selectMfaPending,
} from "../store/slices/authUserSlice";
import { useSelector } from "react-redux";

const AUTH_TOKEN_PARAMETER_KEY = "t";

interface ProtectedRouteProps {
  redirectPathWhenUnauthenticated: string; // the path to redirect to when the user is not authenticated
}

/**
 * A route wrapper that checks if the user is authenticated. It also looks for a token in the URL and ingests it if found to authenticate the user.
 *
 * If a user is already logged in, a token doesn't need to be present in the URL.
 * But a token will override the current user if present.
 *
 * example:
 * https://app.withpotions.com/dashboard?t=<TOKEN>
 *
 * TODO: once the present token is a refresh token, let's fetch a new access token (currently we directly expect an access token in the URL)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  redirectPathWhenUnauthenticated,
}) => {
  const navigate = useNavigate();
  const [isIngesting, setIsIngesting] = useState(false);
  const ingestStarted = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const token: string | null = searchParams.get(AUTH_TOKEN_PARAMETER_KEY);

  const userIsAuthenticated = useSelector(selectAuthenticated);
  const mfaPending = useSelector(selectMfaPending);
  const userAccessTokenIsAlmostExpired = useSelector(selectTokenIsAlmostExpired);
  const accessToken = useSelector(selectAccessToken);

  // Ingest passwordless token from URL if present
  useEffect(() => {
    if (!token || ingestStarted.current) return;
    ingestStarted.current = true;
    setIsIngesting(true);

    ingestPasswordlessAuthToken({ accessToken: `Bearer ${token}` })
      .then((result) => {
        if (result.mfa_required) {
          navigate("/mfa-verify");
        }
      })
      .catch((error) => {
        console.error("Error ingesting token:", error);
        navigate("/login?status=invalid_link");
      })
      .finally(() => {
        setIsIngesting(false);
      });
  }, [token, navigate]);

  // clear the token from the URL
  useEffect(() => {
    if (token) {
      searchParams.delete(AUTH_TOKEN_PARAMETER_KEY);
      setSearchParams(searchParams);
    }
  }, [token, searchParams, setSearchParams]);

  // Redirect if MFA is pending
  useEffect(() => {
    if (mfaPending) navigate("/mfa-verify");
  }, [mfaPending, navigate]);

  useEffect(() => {
    if (!isIngesting && !userIsAuthenticated && !mfaPending) {
      navigate(redirectPathWhenUnauthenticated);
    }
  }, [userIsAuthenticated, isIngesting, mfaPending, navigate, redirectPathWhenUnauthenticated]);

  // at every page navigation, let's check if the access token is almost expired
  // and refresh it if needed
  useEffect(() => {
    if (!(userAccessTokenIsAlmostExpired && userIsAuthenticated)) return;

    refreshToken(accessToken)
    .catch((error) => {
      console.error("Error refreshing token", error);
      });

  }, [userAccessTokenIsAlmostExpired, userIsAuthenticated, accessToken]);

  if (isIngesting) return <></>;

  return userIsAuthenticated ? <Outlet /> : <></>;
};

export default ProtectedRoute;
